import { useFrame, useLoader } from "@react-three/fiber";
import { RigidBody } from "@react-three/rapier";
import { useEffect, useState } from "react";
import * as THREE from "three";
import { Vector3 } from "three";
import { type GLTF, GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { useGameStore } from "../../stores/gameStore";
import { newPosition } from "../../../../utils/newPosition";

type treeType = {
  index: number;
  position: { x: number; z: number };
  model: GLTF;
  rotation: THREE.Euler;
  box: number;
};

interface TreesProps {
  boundary: number;
  count: number;
}

const Trees = ({ boundary, count }: TreesProps) => {
  const { storedTreeData, playerPosition } = useGameStore((state: any) => ({
    storedTreeData: state.storedTreeData,
    playerPosition: state.playerPosition,
  }));

  const treeModels = useLoader(
    GLTFLoader,
    [
      "/models/trees/tree1.glb",
      "/models/trees/tree2.glb",
      "/models/trees/tree3.glb",
      "/models/trees/tree4.glb",
      "/models/trees/tree5.glb",
    ],
    (loader) => {
      // Set the crossOrigin property of the loader to "anonymous"
      // to avoid CORS errors when loading the models from a different origin
      loader.crossOrigin = "anonymous";
    }
  );

  const [trees, setTrees] = useState<treeType[]>([]);

  const [treeDistances, setTreeDistances] = useState<number[]>([]);
  const CLOSE_TREE_RANGE_TO_PLAYER = 20;

  const boxIntersect = (
    minAx: number,
    maxAx: number,
    minAz: number,
    maxAz: number,
    minBx: number,
    maxBx: number,
    minBz: number,
    maxBz: number
  ) => {
    let aLeftOfB = maxAx < minBx;
    let aRightOfB = minAx > maxBx;
    let aAboveB = minAz > maxBz;
    let aBelovB = maxAz < minBz;

    return !(aLeftOfB || aRightOfB || aAboveB || aBelovB);
  };

  const isOverlapping = (index: number, tree: any, trees: any[]) => {
    const minTargetX = tree.position.x - tree.box / 2;
    const maxTargetX = tree.position.x + tree.box / 2;
    const minTargetZ = tree.position.z - tree.box / 2;
    const maxTargetZ = tree.position.z + tree.box / 2;
    for (let i = 0; i < index; i++) {
      let minChildX = trees[i].position.x - trees[i].box / 2;
      let maxChildX = trees[i].position.x + trees[i].box / 2;
      let minChildZ = trees[i].position.z - trees[i].box / 2;
      let maxChildZ = trees[i].position.z + trees[i].box / 2;
      if (
        boxIntersect(
          minTargetX,
          maxTargetX,
          minTargetZ,
          maxTargetZ,
          minChildX,
          maxChildX,
          minChildZ,
          maxChildZ
        )
      ) {
        return true;
      }
    }
    return false;
  };

  const updatePositions = (treeArray: treeType[], boundary: number) => {
    treeArray.forEach((tree, index) => {
      do {
        tree.position.x = newPosition(tree.box, boundary);
        tree.position.z = newPosition(tree.box, boundary);
      } while (isOverlapping(index, tree, treeArray));
      storedTreeData.push([new Vector3(tree.position.x, 0, tree.position.z)]);
    });
    setTrees(treeArray);
  };

  useEffect(() => {
    const tempTrees: treeType[] = [];
    for (let i = 0; i < count; i++) {
      const index = i;
      const model = treeModels[Math.floor(Math.random() * treeModels.length)];
      const rotation = new THREE.Euler(0, Math.random() * 360, 0);
      model.scene.traverse((object) => {
        if (object instanceof THREE.Mesh) {
          object.castShadow = true;
        }
      });
      tempTrees.push({
        index: index,
        position: { x: 0, z: 0 },
        model: model,
        rotation: rotation,
        box: 1,
      });
    }
    updatePositions(tempTrees, boundary);

    const distances = trees.map((tree) => {
      const playerPositionVector = new Vector3(
        playerPosition[0].x,
        0,
        playerPosition[0].z
      );
      const treePosition = new Vector3(tree.position.x, 0, tree.position.z);
      const distanceToPlayer = treePosition.distanceTo(playerPositionVector);
      return distanceToPlayer;
    });
    setTreeDistances(distances);
  }, [boundary, count, treeModels]);

  useFrame((state, delta) => {
    const newDistances = trees.map((tree) => {
      const playerPositionVector = new Vector3(
        playerPosition[0].x,
        0,
        playerPosition[0].z
      );
      const treePosition = new Vector3(tree.position.x, 0, tree.position.z);
      const distanceToPlayer = treePosition.distanceTo(playerPositionVector);
      return distanceToPlayer;
    });
    if (!newDistances.every((value, index) => value === treeDistances[index])) {
      setTreeDistances(newDistances);
    }
  });

  return (
    <group rotation={[0, 0, 0]}>
      {trees.map((tree, index) => {
        const distanceFromCamera = treeDistances[index];
        const shouldRenderFullTree =
          distanceFromCamera <= CLOSE_TREE_RANGE_TO_PLAYER;

        return shouldRenderFullTree ? (
          <RigidBody
            name={"tree"}
            colliders="hull"
            type="fixed"
            userData={{ key: index }}
            key={index}>
            <object3D
              scale={2}
              key={index}
              rotation={tree.rotation}
              position={[tree.position.x, -1.1, tree.position.z]}>
              <primitive object={tree.model.scene.clone()} castShadow={true} />
            </object3D>
          </RigidBody>
        ) : (
          <object3D
            scale={2}
            key={index}
            rotation={tree.rotation}
            position={[tree.position.x, -1.1, tree.position.z]}>
            <primitive object={tree.model.scene.clone()} castShadow={true} />
          </object3D>
        );
      })}
    </group>
  );
};

export default Trees;
